/**
 * Sections de solides — logique pure (Boîte à outils, 3e → 2nde).
 *
 * Un plan glisse dans un pavé droit, un cylindre, une pyramide, un cône ou une
 * boule. Nature de la section, dimensions EXACTES quand elles le sont (sinon
 * « ≈ » au centième), et le raisonnement du cours : section superposable à la
 * face, Pythagore (pavé coupé parallèlement à une arête, cylindre parallèlement
 * à l'axe, boule), réduction de coefficient k (pyramide, cône) et volume de la
 * petite pyramide / du petit cône = k³ × V.
 *
 * Unité : le centimètre. Pavé ABCDEFGH : AB = 6, AD = 4, AE = 3. Cylindre :
 * rayon 3, hauteur 5. Pyramide SABCD : base carrée de côté 6, SO = 6. Cône :
 * rayon 3, SO = 6. Boule : rayon 5.
 */

import { frac, mul, rational, type Frac } from '../../ui/exact';
import { formatNumber } from '../../ui/format';
import type { Face, Plane, V3 } from '../../ui/space3d';

export type SectionSolid = 'cuboid' | 'cylinder' | 'pyramid' | 'cone' | 'sphere';
export type Cut = 'base' | 'side' | 'edge' | 'axis';

export const SECTION_SOLIDS: ReadonlyArray<{ id: SectionSolid; label: string }> = [
  { id: 'cuboid', label: 'Pavé droit' },
  { id: 'cylinder', label: 'Cylindre' },
  { id: 'pyramid', label: 'Pyramide' },
  { id: 'cone', label: 'Cône' },
  { id: 'sphere', label: 'Boule' },
];

export const CUTS: Record<SectionSolid, ReadonlyArray<{ id: Cut; label: string }>> = {
  cuboid: [
    { id: 'base', label: 'Parallèle à la base' },
    { id: 'side', label: 'Parallèle à une face latérale' },
    { id: 'edge', label: 'Parallèle à une arête' },
  ],
  cylinder: [
    { id: 'base', label: 'Parallèle aux bases' },
    { id: 'axis', label: 'Parallèle à l’axe' },
  ],
  pyramid: [{ id: 'base', label: 'Parallèle à la base' }],
  cone: [{ id: 'base', label: 'Parallèle à la base' }],
  sphere: [{ id: 'base', label: 'Plan qui coupe la boule' }],
};

/** dimensions (cm) */
export const DIM = {
  cuboid: { L: 6, W: 4, H: 3 },
  cylinder: { r: 3, h: 5 },
  pyramid: { a: 6, H: 6 },
  cone: { r: 3, H: 6 },
  sphere: { R: 5 },
} as const;

export const ROUND = 48;

export interface Param {
  /** « Hauteur du plan », « Distance à l'axe »… */
  label: string;
  min: number;
  max: number;
  step: number;
  initial: number;
}

export function paramOf(solid: SectionSolid, cut: Cut): Param {
  if (solid === 'cuboid') {
    if (cut === 'side')
      return { label: 'AM (M sur [AB])', min: 0.5, max: 5.5, step: 0.5, initial: 2 };
    if (cut === 'edge')
      return { label: 'BI (I sur [BA])', min: 0.75, max: 3, step: 0.75, initial: 1.5 };
    return { label: 'Hauteur du plan', min: 0.5, max: 2.5, step: 0.5, initial: 1.5 };
  }
  if (solid === 'cylinder')
    return cut === 'axis'
      ? { label: 'Distance OH à l’axe', min: 0, max: 2.7, step: 0.3, initial: 1.8 }
      : { label: 'Hauteur du plan', min: 0.5, max: 4.5, step: 0.5, initial: 2.5 };
  if (solid === 'sphere') return { label: 'Distance OO′', min: 0, max: 4.5, step: 0.5, initial: 3 };
  return { label: 'Hauteur OO′ du plan', min: 0.5, max: 5.5, step: 0.5, initial: 3 };
}

/* ---------- maillages ---------- */

function ring(r: number, z: number): V3[] {
  return Array.from({ length: ROUND }, (_, k): V3 => {
    const t = (2 * Math.PI * k) / ROUND;
    return [r * Math.cos(t), r * Math.sin(t), z];
  });
}

/** sommets nommés de chaque solide (pour les étiquettes) */
export function namedPoints(solid: SectionSolid): Record<string, V3> {
  switch (solid) {
    case 'cuboid': {
      const { L, W, H } = DIM.cuboid;
      return {
        A: [0, 0, 0],
        B: [L, 0, 0],
        C: [L, W, 0],
        D: [0, W, 0],
        E: [0, 0, H],
        F: [L, 0, H],
        G: [L, W, H],
        H: [0, W, H],
      };
    }
    case 'pyramid': {
      const { a, H } = DIM.pyramid;
      const h = a / 2;
      return {
        A: [-h, -h, 0],
        B: [h, -h, 0],
        C: [h, h, 0],
        D: [-h, h, 0],
        S: [0, 0, H],
        O: [0, 0, 0],
      };
    }
    case 'cone':
      return { S: [0, 0, DIM.cone.H], O: [0, 0, 0] };
    case 'cylinder':
      return { O: [0, 0, DIM.cylinder.h] };
    case 'sphere':
      return { O: [0, 0, 0] };
  }
}

/** faces orientées vers l'extérieur ; la boule n'a pas de maillage (dessin exact) */
export function meshOf(solid: SectionSolid): Face[] {
  switch (solid) {
    case 'cuboid': {
      const p = namedPoints('cuboid');
      const f = (...k: string[]): Face => ({ pts: k.map((x) => p[x]!) });
      return [
        f('A', 'D', 'C', 'B'),
        f('E', 'F', 'G', 'H'),
        f('A', 'B', 'F', 'E'),
        f('D', 'H', 'G', 'C'),
        f('A', 'E', 'H', 'D'),
        f('B', 'C', 'G', 'F'),
      ];
    }
    case 'pyramid': {
      const p = namedPoints('pyramid');
      const f = (...k: string[]): Face => ({ pts: k.map((x) => p[x]!) });
      return [
        f('A', 'D', 'C', 'B'),
        f('A', 'B', 'S'),
        f('B', 'C', 'S'),
        f('C', 'D', 'S'),
        f('D', 'A', 'S'),
      ];
    }
    case 'cylinder': {
      const { r, h } = DIM.cylinder;
      const lo = ring(r, 0);
      const hi = ring(r, h);
      return [
        { pts: [...lo].reverse() },
        { pts: hi },
        ...lo.map((p, k) => ({
          pts: [p, lo[(k + 1) % ROUND]!, hi[(k + 1) % ROUND]!, hi[k]!],
          smooth: 1,
        })),
      ];
    }
    case 'cone': {
      const { r, H } = DIM.cone;
      const lo = ring(r, 0);
      const S: V3 = [0, 0, H];
      return [
        { pts: [...lo].reverse() },
        ...lo.map((p, k) => ({ pts: [p, lo[(k + 1) % ROUND]!, S], smooth: 1 })),
      ];
    }
    case 'sphere':
      return [];
  }
}

export function planeOf(solid: SectionSolid, cut: Cut, v: number): Plane {
  if (solid === 'cuboid' && cut === 'side') return { n: [1, 0, 0], d: v };
  if (solid === 'cuboid' && cut === 'edge') {
    // BI = v sur [BA], BJ = 4v/3 sur [BC] : 4x − 3y = 4(6 − v)
    const L = DIM.cuboid.L;
    return { n: [4 / 5, -3 / 5, 0], d: (4 * (L - v)) / 5 };
  }
  if (solid === 'cylinder' && cut === 'axis') return { n: [1, 0, 0], d: v };
  return { n: [0, 0, 1], d: v };
}

/* ---------- explications ---------- */

const cm = (v: number) => `${formatNumber(v, 2)} cm`;
const n2 = (v: number) => formatNumber(v, 4);
const sq = (v: number) => (v < 0 ? `(${n2(v)})²` : `${n2(v)}²`);

/** √(n / 100) exacte si n est un carré parfait, sinon ≈ au centième */
function rootHundredths(n: number): { exact: boolean; value: number; text: string } {
  const r = Math.round(Math.sqrt(n));
  const exact = r * r === n;
  const value = Math.sqrt(n) / 10;
  return exact
    ? { exact, value, text: cm(r / 10) }
    : { exact, value, text: `√${n2(n / 100)} ≈ ${cm(Math.round(value * 100) / 100)}` };
}

export type Shape =
  | { kind: 'rect'; w: number; h: number; wLabel: string; hLabel: string }
  | { kind: 'disk'; r: number; rLabel: string };

export interface SectionInfo {
  /** « un rectangle », « un carré », « un disque » */
  name: string;
  shape: Shape;
  lines: string[];
}

const fracText = (f: Frac) => rational(f.p, f.q).plain;
/** décimal s'il a au plus 2 décimales (27/4 → 6,75), sinon fraction (243/32) */
const short = (f: Frac) => (100 % f.q === 0 ? formatNumber(f.p / f.q, 2) : `${f.p}/${f.q}`);
/** fraction exacte, suivie de sa valeur approchée si elle ne tombe pas juste */
const withApprox = (f: Frac) => {
  const t = short(f);
  return t.includes('/') ? `${t} ≈ ${formatNumber(f.p / f.q, 2)}` : t;
};
/** « 9π » ou « 243/32 × π » */
const timesPi = (f: Frac) => {
  const t = short(f);
  return t.includes('/') ? `${t} × π` : `${t}π`;
};

export function sectionInfo(solid: SectionSolid, cut: Cut, v: number): SectionInfo {
  switch (solid) {
    case 'cuboid': {
      const { L, W, H } = DIM.cuboid;
      if (cut === 'base')
        return {
          name: 'un rectangle',
          shape: { kind: 'rect', w: L, h: W, wLabel: cm(L), hLabel: cm(W) },
          lines: [
            'La section est un rectangle superposable à la base ABCD, à n’importe quelle hauteur.',
            `Section : rectangle de ${cm(L)} sur ${cm(W)}.`,
          ],
        };
      if (cut === 'side')
        return {
          name: 'un rectangle',
          shape: { kind: 'rect', w: W, h: H, wLabel: cm(W), hLabel: cm(H) },
          lines: [
            'Le plan est parallèle à la face ADHE : la section est un rectangle superposable à cette face.',
            `Section : rectangle de ${cm(W)} sur ${cm(H)}.`,
          ],
        };
      const bi = v;
      const bj = (4 * v) / 3;
      const ij = (5 * v) / 3;
      return {
        name: 'un rectangle',
        shape: { kind: 'rect', w: ij, h: H, wLabel: `IJ = ${cm(ij)}`, hLabel: cm(H) },
        lines: [
          'Le plan est parallèle à l’arête [BF] : la section est un rectangle, de côtés [IJ] et la hauteur du pavé.',
          `Il coupe la base ABCD selon [IJ], avec I sur [BA] et J sur [BC] : BI = ${cm(bi)} et BJ = ${cm(bj)}.`,
          `Le triangle BIJ est rectangle en B : IJ² = BI² + BJ² = ${sq(bi)} + ${sq(bj)} = ${n2(ij * ij)}, donc IJ = ${cm(ij)} (théorème de Pythagore).`,
          `Section : rectangle de ${cm(ij)} sur ${cm(H)}.`,
        ],
      };
    }
    case 'cylinder': {
      const { r, h } = DIM.cylinder;
      if (cut === 'base')
        return {
          name: 'un disque',
          shape: { kind: 'disk', r, rLabel: `r = ${cm(r)}` },
          lines: [
            'Le plan est parallèle aux bases : la section est un disque de même rayon que les bases.',
            `Section : disque de rayon ${cm(r)}.`,
          ],
        };
      const d10 = Math.round(v * 10);
      const n = 100 * r * r - d10 * d10;
      const hm = rootHundredths(n);
      const mn = 2 * hm.value;
      const mnText = hm.exact ? cm(mn) : `≈ ${cm(Math.round(mn * 100) / 100)}`;
      return {
        name: 'un rectangle',
        shape: {
          kind: 'rect',
          w: mn,
          h,
          wLabel: `MN ${hm.exact ? '= ' : ''}${mnText}`,
          hLabel: cm(h),
        },
        lines: [
          d10 === 0
            ? 'Le plan contient l’axe du cylindre : la section est un rectangle dont un côté est un diamètre.'
            : 'Le plan est parallèle à l’axe : la section est un rectangle MNPQ, de hauteur celle du cylindre.',
          d10 === 0
            ? `MN = 2 × r = ${cm(mn)}.`
            : `H, milieu de [MN], est le pied de la perpendiculaire à (MN) passant par O : le triangle OHM est rectangle en H, donc HM² = OM² − OH² = ${sq(r)} − ${sq(v)} = ${n2(n / 100)}, donc ${
                hm.exact
                  ? `HM = ${hm.text} et MN = 2 × HM = ${mnText}.`
                  : `HM = √${n2(n / 100)} cm et MN = 2 × HM = 2√${n2(n / 100)} ${mnText}.`
              }`,
          hm.exact
            ? `Section : rectangle de ${mnText} sur ${cm(h)}.`
            : `Section : rectangle d’environ ${mnText.replace('≈ ', '')} sur ${cm(h)}.`,
        ],
      };
    }
    case 'pyramid':
    case 'cone': {
      const H = solid === 'pyramid' ? DIM.pyramid.H : DIM.cone.H;
      const k = frac(Math.round((H - v) * 2), H * 2);
      const kt = fracText(k);
      const kv = k.p / k.q;
      const k3 = mul(mul(k, k), k);
      if (solid === 'pyramid') {
        const a = DIM.pyramid.a;
        const side = kv * a;
        const V = (a * a * H) / 3;
        const small = mul(k3, frac(V));
        return {
          name: 'un carré',
          shape: { kind: 'rect', w: side, h: side, wLabel: `A′B′ = ${cm(side)}`, hLabel: '' },
          lines: [
            'Le plan est parallèle à la base : la section A′B′C′D′ est un carré, réduction de la base ABCD.',
            `Coefficient de réduction : k = SO′ / SO = ${n2(H - v)} / ${n2(H)} = ${kt}.`,
            `A′B′ = k × AB = ${kt} × ${n2(a)} = ${cm(side)}.`,
            `Volume de la pyramide SABCD : (${n2(a)} × ${n2(a)} × ${n2(H)}) ÷ 3 = ${n2(V)} cm³ ; celui de la petite pyramide SA′B′C′D′ : k³ × ${n2(V)} = ${withApprox(small)} cm³.`,
          ],
        };
      }
      const r = DIM.cone.r;
      const rr = kv * r;
      const V18 = (r * r * H) / 3; // V = 18π
      const small = mul(k3, frac(V18));
      return {
        name: 'un disque',
        shape: { kind: 'disk', r: rr, rLabel: `O′A′ = ${cm(rr)}` },
        lines: [
          'Le plan est parallèle à la base : la section est un disque de centre O′, réduction du disque de base.',
          `Coefficient de réduction : k = SO′ / SO = ${n2(H - v)} / ${n2(H)} = ${kt}.`,
          `O′A′ = k × OA = ${kt} × ${n2(r)} = ${cm(rr)}.`,
          `Volume du cône : (π × ${n2(r)}² × ${n2(H)}) ÷ 3 = ${n2(V18)}π cm³ ; celui du petit cône : k³ × ${n2(V18)}π = ${timesPi(small)} ≈ ${formatNumber((small.p / small.q) * Math.PI, 2)} cm³.`,
        ],
      };
    }
    case 'sphere': {
      const R = DIM.sphere.R;
      const d10 = Math.round(v * 10);
      const n = 100 * R * R - d10 * d10;
      const om = rootHundredths(n);
      if (d10 === 0)
        return {
          name: 'un disque',
          shape: { kind: 'disk', r: R, rLabel: `r = ${cm(R)}` },
          lines: [
            'Le plan passe par le centre O : la section est un grand cercle (pour la sphère), un grand disque (pour la boule), de même rayon que la boule.',
            `Section : disque de rayon ${cm(R)}.`,
          ],
        };
      return {
        name: 'un disque',
        shape: {
          kind: 'disk',
          r: om.value,
          rLabel: `O′M ${om.exact ? '= ' : ''}${om.text.replace(/^√[^≈]+/, '')}`,
        },
        lines: [
          'La section d’une sphère par un plan est un cercle ; celle d’une boule, un disque. Son centre O′ est le pied de la perpendiculaire au plan passant par O.',
          `Le triangle OO′M est rectangle en O′ : O′M² = OM² − OO′² = ${sq(R)} − ${sq(v)} = ${n2(n / 100)}, donc O′M = ${om.text}.`,
        ],
      };
    }
  }
}
